import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";

export default function PrivacyPage() {
  return (
    <>
      <SEO title="Privacy Policy" description="Privacy Policy for Finance Topia. Cookies, Google AdSense, and your data rights." url="/privacy" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Privacy" }]} />
        <header className="border-b-2 border-foreground pb-6 mt-4">
          <div className="overline">Legal</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tight">Privacy Policy</h1>
          <p className="mt-2 font-mono text-xs text-foreground/60">Last updated: {new Date().toLocaleDateString()}</p>
        </header>
        <div className="prose-article mt-10">
          <p>Finance Topia ("we", "us", "our") is committed to protecting your privacy. This policy explains what data we collect when you visit <strong>finance-topia.com</strong>, how it's used, who we share it with, and the choices you have.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">1. Information We Collect</h2>
          <p><strong>Information you provide directly:</strong></p>
          <ul>
            <li>Your email address if you subscribe to our newsletter.</li>
            <li>The name, email, subject, and message you provide when using our Contact form.</li>
          </ul>
          <p><strong>Information collected automatically:</strong></p>
          <ul>
            <li>Standard server access logs: IP address, browser user-agent, referring page, timestamps.</li>
            <li>Cookies and similar technologies set by us and by third parties (Google Analytics, Google AdSense) — see section 3.</li>
          </ul>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">2. How We Use Your Data</h2>
          <ul>
            <li>To send you our newsletter, if you subscribed.</li>
            <li>To reply to messages you send through our Contact form.</li>
            <li>To improve editorial content and site performance.</li>
            <li>To measure aggregate audience size and behavior via Google Analytics.</li>
            <li>To serve advertising through Google AdSense (see section 3).</li>
            <li>To comply with legal obligations.</li>
          </ul>
          <p>We do not sell your personal information.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">3. Cookies, Google AdSense, and Third-Party Vendors</h2>
          <p>This site uses cookies and similar technologies:</p>
          <ul>
            <li><strong>Essential cookies</strong> for basic site functionality (session, consent state).</li>
            <li><strong>Analytics cookies</strong> via Google Analytics 4 (measurement ID <code>G-EYS52NKLPW</code>) to understand how visitors use the site.</li>
            <li><strong>Advertising cookies</strong> via <strong>Google AdSense</strong>, publisher ID <code>ca-pub-8745708845539751</code>.</li>
          </ul>
          <p><strong>How Google uses your data:</strong></p>
          <ul>
            <li>Google, as a third-party vendor, uses cookies to serve ads on Finance Topia.</li>
            <li>Google's use of the DoubleClick cookie enables it and its partners to serve ads to our users based on their visit to Finance Topia and/or other sites on the Internet.</li>
            <li>Third-party vendors, including Google, may use cookies to serve ads based on your prior visits to this or other websites.</li>
          </ul>
          <p><strong>Your Google ad choices:</strong></p>
          <ul>
            <li>You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.</li>
            <li>You may opt out of a third-party vendor's use of cookies for personalized ads by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">www.aboutads.info</a> (US) or <a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer">www.youronlinechoices.com</a> (EU).</li>
            <li>You may read Google's own <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">"How Google uses information from sites that use our services"</a> policy for full detail.</li>
          </ul>
          <p><strong>Consent management:</strong> a cookie consent banner appears on your first visit and lets you accept or reject non-essential cookies. Your choice is stored in your browser and applied via Google Consent Mode v2 to both AdSense and Analytics.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">4. Legal Basis for Processing (EU/UK visitors)</h2>
          <ul>
            <li><strong>Consent</strong> — for non-essential cookies and marketing communications (you can withdraw it at any time by adjusting your cookie preferences or unsubscribing).</li>
            <li><strong>Legitimate interests</strong> — for maintaining editorial quality and site security.</li>
            <li><strong>Legal obligation</strong> — for retaining records required by law.</li>
          </ul>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">5. Data Sharing</h2>
          <p>We share data only with:</p>
          <ul>
            <li>Our infrastructure providers (Emergent — hosting, MongoDB Atlas — database, Cloudflare — CDN & DDoS protection) strictly for site operation.</li>
            <li>Google (Analytics, AdSense, Fonts).</li>
            <li>Law enforcement, when compelled by a valid legal request.</li>
          </ul>
          <p>We do not sell, rent, or trade your personal information for advertising to third parties beyond what is described above.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">6. Data Retention</h2>
          <ul>
            <li>Newsletter emails: retained until you unsubscribe.</li>
            <li>Contact form messages: retained for up to 24 months, then deleted.</li>
            <li>Server logs: retained for up to 90 days.</li>
            <li>Analytics data: 14 months in Google Analytics 4 (default retention).</li>
          </ul>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">7. Your Rights</h2>
          <p>Depending on where you live, you may have the right to:</p>
          <ul>
            <li>Access or receive a copy of the personal data we hold about you.</li>
            <li>Correct inaccurate data.</li>
            <li>Delete your data ("right to be forgotten").</li>
            <li>Object to or restrict certain processing.</li>
            <li>Withdraw consent for cookies at any time.</li>
            <li>Lodge a complaint with a supervisory authority (EU/UK).</li>
          </ul>
          <p>To exercise any of these rights, email <a href="mailto:editorial@finance-topia.com">editorial@finance-topia.com</a>. We respond within 30 days.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">8. Children</h2>
          <p>Finance Topia is not directed at children under 13. We do not knowingly collect data from anyone under 13. If you believe we have inadvertently collected such data, contact us and we will delete it.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">9. International Transfers</h2>
          <p>Your data may be processed in countries other than your own, including the United States. We rely on standard contractual clauses and vendor certifications (e.g., Google's participation in the EU-U.S. Data Privacy Framework) where applicable.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">10. Changes to This Policy</h2>
          <p>We may update this policy periodically. Material changes will be reflected in the "Last updated" date above. Continued use of the site after changes constitutes acceptance.</p>

          <h2 className="font-display text-2xl font-black mt-8 mb-3">11. Contact</h2>
          <p>Questions about this policy or your data? Email <a href="mailto:editorial@finance-topia.com">editorial@finance-topia.com</a> or use our <a href="/contact">Contact page</a>.</p>
        </div>
      </div>
    </>
  );
}
